import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Map, Marker, Source, Layer, NavigationControl } from 'react-map-gl';
import { X } from 'lucide-react';
import 'mapbox-gl/dist/mapbox-gl.css';
import { pmToAqi, getAqiCategory } from '../utils/airQualityUtils';
import { locationMap, ZIP_TO_TOWN } from '../config/locations';
import zipBoundaries from '../config/zipBoundaries.geo.json';
import zipBounds from '../config/zipBounds.json';

const locationData = locationMap;

// Bounding box of St. Mary's County, used to reset the view when no filter is set.
const COUNTY_BOUNDS = [[-76.872, 38.0379], [-76.3207, 38.5139]];

// Zip (ZCTA) boundaries: thin borders always shown, plus a highlighted fill/border
// for the zip(s) matching the active area/zip filter.
const zipLineLayer = {
  id: 'zip-line',
  type: 'line',
  paint: { 'line-color': '#6b7280', 'line-opacity': 0.35, 'line-width': 0.6 }
};

// Invisible fill used purely as the hit area for hover/click on each zip.
const zipInteractiveFillLayer = {
  id: 'zip-fill',
  type: 'fill',
  paint: { 'fill-color': '#000000', 'fill-opacity': 0 }
};

// Highlight shown while hovering a zip shape.
const zipHoverFillLayer = {
  id: 'zip-hover-fill',
  type: 'fill',
  paint: { 'fill-color': '#2563eb', 'fill-opacity': 0.18 }
};

const zipHoverLineLayer = {
  id: 'zip-hover-line',
  type: 'line',
  paint: { 'line-color': '#2563eb', 'line-opacity': 0.8, 'line-width': 1.5 }
};

const zipHighlightFillLayer = {
  id: 'zip-highlight-fill',
  type: 'fill',
  paint: { 'fill-color': '#2563eb', 'fill-opacity': 0.15 }
};

const zipHighlightLineLayer = {
  id: 'zip-highlight-line',
  type: 'line',
  paint: { 'line-color': '#2563eb', 'line-opacity': 0.9, 'line-width': 2 }
};

const getAirQualityColor = (pm25) => {
  const { color } = getAqiCategory(pmToAqi(pm25));
  return color;
};

const InteractiveMap = ({
  statistics,
  loading,
  onLocationSelect,
  selectedLocation,
  setIsToolbarOpen,
  isToolbarOpen,
  visibleLocationIds,
  activeZips,
  onRegionSelect
}) => {
  const mapRef = useRef(null);
  const [hoveredMarkerId, setHoveredMarkerId] = useState(null);
  const [hoveredZip, setHoveredZip] = useState(null);
  const [viewState, setViewState] = useState({
    longitude: -76.5258,
    latitude: 38.2897,
    zoom: 9
  });

  const visibleSet = useMemo(
    () => (visibleLocationIds ? new Set(visibleLocationIds.map(String)) : null),
    [visibleLocationIds]
  );

  // Zip(s) covered by the active filter, used to highlight matching zip polygons.
  const highlightZips = activeZips;

  // Stable key so the zoom effect only fires when the selected zip set changes.
  const activeZipsKey = activeZips ? [...activeZips].sort().join(',') : '';

  // Pan/zoom to the selected zip shapes (or back to the whole county when cleared).
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    if (!activeZips || activeZips.length === 0) {
      map.fitBounds(COUNTY_BOUNDS, { padding: 30, duration: 600 });
      return;
    }

    // Union of the selected zips' polygon bounding boxes.
    let w = Infinity, s = Infinity, e = -Infinity, n = -Infinity;
    activeZips.forEach(z => {
      const b = zipBounds[z];
      if (!b) return;
      w = Math.min(w, b[0][0]); s = Math.min(s, b[0][1]);
      e = Math.max(e, b[1][0]); n = Math.max(n, b[1][1]);
    });
    if (w === Infinity) return;

    map.fitBounds([[w, s], [e, n]], { padding: 40, maxZoom: 13, duration: 600 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeZipsKey]);

  const crosshairLinesGeoJSON = useMemo(() => {
    if (!selectedLocation?.id || !locationData[selectedLocation.id]) return null;

    const location = locationData[selectedLocation.id];
    const { lng, lat } = location;
    const extensionFactor = 1;
    const lngDelta = (viewState.bounds?.ne?.[0] - viewState.bounds?.sw?.[0]) * extensionFactor || 0.5;
    const latDelta = (viewState.bounds?.ne?.[1] - viewState.bounds?.sw?.[1]) * extensionFactor || 0.5;

    return {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates: [[lng, lat - latDelta], [lng, lat + latDelta]]
          }
        },
        {
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates: [[lng - lngDelta, lat], [lng + lngDelta, lat]]
          }
        }
      ]
    };
  }, [selectedLocation, viewState.bounds]);

  const crosshairLayer = {
    id: 'crosshair-lines',
    type: 'line',
    paint: {
      'line-color': '#000000',
      'line-opacity': 0.3,
      'line-width': 1,
      'line-dasharray': [3, 3]
    }
  };

  const getLocationAverages = () => {
    if (!statistics?.locations) return {};
    const locationAverages = {};
    Object.entries(statistics.locations).forEach(([locationId, locationData]) => {
      if (locationData.hour?.adjustedAverage !== undefined) {
        locationAverages[locationId] = {
          adjustedAverage: locationData.hour.adjustedAverage,
          actualAverage: locationData.hour.rawAverage,
          name: locationData.name,
          fullName: locationData.fullName,
          latestTimestamp: locationData.hour?.timestamp,
          sensorCount: locationData.sensorCount
        };
      }
    });
    return locationAverages;
  };

  const locationAverages = getLocationAverages();

  const handleMapMouseMove = (e) => {
    const feature = e.features && e.features[0];
    setHoveredZip(feature ? feature.properties.zip : null);
  };

  const handleMapClick = (e) => {
    if (!onRegionSelect) return;
    const feature = e.features && e.features[0];
    if (!feature) return;
    const zip = feature.properties.zip;
    // Toggle: clicking the already-selected (zoomed-in) zip clears the filter
    // and zooms back out to the whole county.
    if (activeZips && activeZips.includes(zip)) {
      onRegionSelect({ town: null, zipCode: null });
    } else {
      onRegionSelect({ zipCode: zip, town: ZIP_TO_TOWN[zip] || null });
    }
  };

  return (
    <div className="w-full h-96 relative rounded-lg overflow-hidden">
      <Map
        ref={mapRef}
        {...viewState}
        onMove={evt => setViewState(evt.viewState)}
        style={{ width: '100%', height: '100%' }}
        mapStyle="mapbox://styles/mapbox/light-v11"
        mapboxAccessToken={process.env.REACT_APP_MAPBOX_TOKEN}
        scrollZoom={false} // Disable scroll zoom
        interactiveLayerIds={['zip-fill']}
        cursor={hoveredZip ? 'pointer' : 'grab'}
        onMouseMove={handleMapMouseMove}
        onMouseLeave={() => setHoveredZip(null)}
        onClick={handleMapClick}
      >
        {/* Zip (ZCTA) boundaries: invisible hit area + borders + selection & hover highlight */}
        <Source id="zip-boundaries" type="geojson" data={zipBoundaries}>
          <Layer {...zipInteractiveFillLayer} />
          <Layer {...zipLineLayer} />
          {highlightZips && highlightZips.length > 0 && (
            <Layer
              {...zipHighlightFillLayer}
              filter={['in', ['get', 'zip'], ['literal', highlightZips]]}
            />
          )}
          {highlightZips && highlightZips.length > 0 && (
            <Layer
              {...zipHighlightLineLayer}
              filter={['in', ['get', 'zip'], ['literal', highlightZips]]}
            />
          )}
          {hoveredZip && (
            <Layer
              {...zipHoverFillLayer}
              filter={['==', ['get', 'zip'], hoveredZip]}
            />
          )}
          {hoveredZip && (
            <Layer
              {...zipHoverLineLayer}
              filter={['==', ['get', 'zip'], hoveredZip]}
            />
          )}
        </Source>

        {/* Add Navigation Controls */}
        <NavigationControl
          position="bottom-right"
          showCompass={false} // Only show zoom controls, hide compass
        />

        {crosshairLinesGeoJSON && (
          <Source type="geojson" data={crosshairLinesGeoJSON}>
            <Layer {...crosshairLayer} />
          </Source>
        )}

        {Object.entries(locationData).map(([id, location]) => {
          const data = locationAverages[id];
          const isActive = selectedLocation?.id?.toString() === id.toString() && isToolbarOpen;
          const isHovered = hoveredMarkerId === id;
          const isDimmed = visibleSet ? !visibleSet.has(id.toString()) : false;
          const displayValue = data?.adjustedAverage;
          const color = getAirQualityColor(displayValue);

          return (
            <Marker
              key={id}
              longitude={location.lng}
              latitude={location.lat}
              anchor="center"
              onClick={(evt) => {
                // Stop the click from reaching the map's zip handler underneath,
                // so selecting a sensor never toggles the zip zoom.
                evt.originalEvent.stopPropagation();
                onLocationSelect(id, evt.originalEvent);
              }}
            >
              <div
                className="relative location-marker"
                style={{ opacity: isDimmed ? 0.25 : 1, transition: 'opacity 200ms' }}
              >
                <div
                  className={`
                    w-3 h-3 rounded-full border-2 cursor-pointer transform transition-all duration-200
                    ${isActive || isHovered ? 'z-50' : 'z-10'}
                    ${isActive ? 'border-black shadow-lg scale-150' : 'border-white shadow hover:scale-125'}
                  `}
                  style={{ backgroundColor: color }}
                  onMouseEnter={() => setHoveredMarkerId(id)}
                  onMouseLeave={() => setHoveredMarkerId(null)}
                />

                {(isActive || isHovered) && data && (
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-1 px-2 py-1 bg-black bg-opacity-75 text-white text-xs rounded whitespace-nowrap z-50 pointer-events-none">
                    <div className="font-medium">{data.fullName || location.name}</div>
                    {data.adjustedAverage && (
                      <div>{data.adjustedAverage.toFixed(1)} µg/m³</div>
                    )}
                  </div>
                )}
              </div>
            </Marker>
          );
        })}
      </Map>

      {loading && (
        <div className="absolute top-4 left-1/2 transform -translate-x-1/2 bg-white px-4 py-2 rounded-lg shadow-lg">
          Loading data...
        </div>
      )}

      {isToolbarOpen && selectedLocation && (
        <button
          onClick={() => {
            setIsToolbarOpen(false);
            onLocationSelect(null);
          }}
          className="absolute top-4 right-4 bg-white p-2 rounded-full shadow-lg hover:bg-gray-100 transition-colors"
          aria-label="Close location details"
        >
          <X size={16} />
        </button>
      )}
    </div>
  );
};

export default InteractiveMap;